import { GeckoColorScale } from 'color-scale-kit'
import { AssetVizMode } from 'features/assetViz/defines'
import { useCdfBinReading } from 'features/dataLayers'
import { useGeckoLocale } from 'features/locale'
import React from 'react'
import styled from 'styled-components'
import { Color } from 'three'
import {
  DataType,
  GeckoComponent,
  GeckoComponentCoords,
  GeckoComponentDataFrame,
  GeckoComponentModel,
  GeckoComponentModelType,
  GeckoComponentShape,
  GeckoFeature,
  GeckoLegacyBoilerWall,
  GeckoMarkupLayer,
  GeckoMarkupPrimitive,
  GeckoMeasurement,
  GeckoMeasurementUnit,
  GeckoPolygonalPlate,
  GeckoShellCourse,
  GeckoShipDeckCompartment,
  GeckoSource,
  GeckoView,
} from 'types'

import { GeckoGeometryLineItem } from './GeckoGeometryLineItem'
import { GeckoMeasurementLineItem } from './GeckoMeasurementLineItem'

export const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  width: 100%;
  height: 2rem;
`

export const ComponentName = styled.div`
  font-weight: 700;
  font-size: 1rem;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`

const TooltipBadge = styled.div<{
  $colorString?: string
  $colorIsBright?: boolean
}>`
  max-width: 15rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 2px;
  padding: 0.25rem 1rem;
  border-radius: 1rem;
  background-color: ${(props) =>
    props.$colorString ?? props.theme.forecast.background3};

  color: ${(props) =>
    props.$colorIsBright
      ? props.theme.forecast.background1
      : props.theme.forecast.text1};
`

const StyledDataView = styled.span<{
  $colorIsBright?: boolean
}>`
  flex: 10;
  text-overflow: ellipsis;
  overflow: hidden;
`

const StyledFormattedValue = styled.span`
  flex: 1;
`

export const LabelsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`

export const DefaultVizTooltipContent: React.FC<{
  selectedComponent: GeckoComponent | undefined
  selectedComponentModel: GeckoComponentModel | undefined
  selectedCDF: GeckoComponentDataFrame | undefined
  relevantThicknessCDF: GeckoComponentDataFrame | undefined
  relevantNominalCDF: GeckoComponentDataFrame | undefined
  selectedColorScale: GeckoColorScale | undefined
  hoveredCoords: GeckoComponentCoords | undefined
  hoveredFeature: GeckoFeature | undefined
  draftSelection?:
    | GeckoComponentShape
    | GeckoMarkupLayer
    | GeckoMarkupPrimitive
    | GeckoFeature
    | undefined
  assetVizMode?: AssetVizMode
  overrideLabelsContentDOM?: React.ReactNode
}> = ({
  selectedComponent,
  selectedComponentModel,
  selectedCDF,
  relevantThicknessCDF,
  relevantNominalCDF,
  selectedColorScale,
  hoveredCoords,
  hoveredFeature,
  draftSelection,
  assetVizMode,
  overrideLabelsContentDOM,
}) => {
  //----------------------------------------------------------------------------

  const { title, coords } = React.useMemo(() => {
    const id = selectedComponent?.id ?? ''
    let title = selectedComponent?.data.name ?? 'Asset'

    let x: GeckoMeasurement | undefined = undefined
    let y: GeckoMeasurement | undefined = undefined
    if (hoveredCoords) {
      x = hoveredCoords.x
      y = hoveredCoords.y
    }
    if (hoveredFeature instanceof GeckoShellCourse) {
      title = hoveredFeature.name
    } else if (hoveredFeature instanceof GeckoLegacyBoilerWall) {
      title = hoveredFeature.name
      if (x) x = hoveredFeature.convertToCustomerTubeNumbers(x)
    }
    if (!x || !y) return { title, coords: undefined }

    const coords = GeckoComponentCoords.CreateFromMeasurements(id, x, y)
    return { title, coords }
  }, [selectedComponent, hoveredFeature, hoveredCoords])

  //----------------------------------------------------------------------------

  const { value: selectedValue, isLoading } = useCdfBinReading(
    selectedCDF,
    hoveredCoords
  )
  const { value: thicknessValue } = useCdfBinReading(
    relevantThicknessCDF,
    hoveredCoords
  )
  const { value: nominalValue } = useCdfBinReading(
    relevantNominalCDF,
    hoveredCoords
  )

  const { colorString, colorIsBright } = React.useMemo(() => {
    if (selectedValue === undefined || !selectedColorScale) {
      return { color: undefined, colorIsBright: false }
    } else {
      const colorString = selectedColorScale.getColorForValue(selectedValue.num)
      let { r, g, b } = new Color(colorString).convertLinearToSRGB()

      // Transforms per https://www.w3.org/TR/WCAG21/#contrast-minimum
      if (r <= 0.04045) r = r / 12.92
      else r = Math.pow((r + 0.055) / 1.055, 2.4)
      if (g <= 0.04045) g = g / 12.92
      else g = Math.pow((g + 0.055) / 1.055, 2.4)
      if (b <= 0.04045) b = b / 12.92
      else b = Math.pow((b + 0.055) / 1.055, 2.4)

      const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b
      // Based on whether contrast is higher with black or white
      const colorIsBright = luma > 0.1791

      return { colorString, colorIsBright }
    }
  }, [selectedColorScale, selectedValue])

  const { formatMeasurement } = useGeckoLocale()

  //----------------------------------------------------------------------------
  // Check if we're hovering over a ship deck feature in AllFeaturesSelectable mode (while Asset Model Editor is open)
  const isShipDeckFeatureHover =
    assetVizMode === AssetVizMode.AllFeaturesSelectable &&
    selectedComponentModel?.type === GeckoComponentModelType.ShipDeck &&
    draftSelection instanceof GeckoFeature

  const hoveredPlate =
    isShipDeckFeatureHover && draftSelection instanceof GeckoPolygonalPlate
      ? draftSelection
      : undefined

  const hoveredCompartment =
    isShipDeckFeatureHover && draftSelection instanceof GeckoShipDeckCompartment
      ? draftSelection
      : undefined

  const hoveredStructure =
    isShipDeckFeatureHover &&
    !(draftSelection instanceof GeckoPolygonalPlate) &&
    !(draftSelection instanceof GeckoShipDeckCompartment)
      ? draftSelection
      : undefined

  //----------------------------------------------------------------------------

  return (
    <>
      <HeaderContainer>
        <ComponentName>
          {hoveredPlate
            ? hoveredPlate.name
            : hoveredCompartment
              ? `Compartment: ${hoveredCompartment.name}`
              : hoveredStructure && 'name' in hoveredStructure
                ? (hoveredStructure as { name: string }).name
                : title}
        </ComponentName>
        {hoveredPlate ||
        hoveredCompartment ||
        hoveredStructure ? null : isLoading ? (
          <TooltipBadge>Loading…</TooltipBadge>
        ) : !selectedCDF ? (
          <TooltipBadge>No Data</TooltipBadge>
        ) : (
          <TooltipBadge
            $colorString={colorString}
            $colorIsBright={colorIsBright}>
            {selectedValue?.unit.dataType !== DataType.Categorical && (
              <StyledDataView $colorIsBright={colorIsBright}>
                {
                  // Nominal thickness and T-min thickness have special formatting
                  selectedCDF.data.params.view === GeckoView.Thickness &&
                  (selectedCDF.data.params.source === GeckoSource.Nominal ||
                    selectedCDF.data.params.source === GeckoSource.TMin)
                    ? selectedCDF.data.params.source.name
                    : selectedCDF.data.params.view.name
                }
                :
              </StyledDataView>
            )}
            <StyledFormattedValue>
              {formatMeasurement({ measurement: selectedValue })}
            </StyledFormattedValue>
          </TooltipBadge>
        )}
      </HeaderContainer>
      <hr />
      {overrideLabelsContentDOM ? (
        overrideLabelsContentDOM
      ) : hoveredPlate ? (
        <LabelsContainer>
          <GeckoMeasurementLineItem
            name='Thickness'
            measurement={
              hoveredPlate.wallNominalThickness
                ? hoveredPlate.wallNominalThickness.convertTo(
                    GeckoMeasurementUnit.Inches
                  )
                : undefined
            }
          />
        </LabelsContainer>
      ) : hoveredCompartment || hoveredStructure ? null : (
        <LabelsContainer>
          <GeckoGeometryLineItem
            geometry={coords}
            componentModelType={selectedComponentModel?.type}
          />
          <GeckoMeasurementLineItem name='Nominal' measurement={nominalValue} />
          <GeckoMeasurementLineItem
            name={GeckoView.Thickness.name}
            measurement={thicknessValue}
          />
          {!thicknessValue || !nominalValue ? null : (
            <GeckoMeasurementLineItem
              name={GeckoView.DifferenceFromNominal.name}
              measurement={thicknessValue.minus(nominalValue)}
            />
          )}
        </LabelsContainer>
      )}
    </>
  )
}
